import { useEffect, useState } from "react";
import { useGame } from "./store/useGame";
import { Cockpit } from "./components/Cockpit";
import {
  ArchetypeSelector,
  ARCHETYPE_CHOICE_KEY,
  persistArchetypeChoice,
} from "./components/ArchetypeSelector";
import {
  InvestitureOverlay,
  resetInvestitureSeen,
  shouldShowInvestiture,
} from "./components/InvestitureOverlay";
import { moralRegime } from "./engine/moral-regime";

export function App() {
  const status = useGame((s) => s.status);
  const error = useGame((s) => s.error);
  const worldState = useGame((s) => s.worldState);
  const pledges = useGame((s) => s.pledges);
  const headlines = useGame((s) => s.headlineFeed);
  const lastRevelations = useGame((s) => s.lastRevelations);
  const boot = useGame((s) => s.boot);

  // N1 — l'archétype est-il déjà choisi ? Sinon on attend que le
  // joueur sélectionne avant de booter le moteur. Le `?` initial
  // évite un flash du sélecteur pour les sessions qui ont déjà choisi.
  const [needsArchetype, setNeedsArchetype] = useState<boolean | null>(null);

  // Séquence d'investiture (concept de la boucle) — affichée au T0 d'une
  // nouvelle partie, juste après le choix de l'état du pays. Le flag est
  // réinitialisé au moment du choix d'archétype (cf. onSelect) pour qu'elle
  // accompagne chaque nouvelle partie sans réapparaître à chaque refresh.
  const [showInvestiture, setShowInvestiture] = useState<boolean>(false);
  useEffect(() => {
    if (status === "ready" && worldState && worldState.turn === 0) {
      if (shouldShowInvestiture()) {
        setShowInvestiture(true);
      }
    }
  }, [status, worldState]);

  useEffect(() => {
    // Réévalue le besoin de sélection à chaque transition "idle"
    // (boot initial ou reset) — sinon le sélecteur ne revient pas
    // après "Nouvelle partie".
    if (status === "idle") {
      try {
        const raw = localStorage.getItem(ARCHETYPE_CHOICE_KEY);
        setNeedsArchetype(!raw);
      } catch {
        setNeedsArchetype(false);
      }
    }
  }, [status]);

  useEffect(() => {
    if (needsArchetype === false && status === "idle") boot();
  }, [boot, needsArchetype, status]);

  // L6 — titre qui mute silencieusement selon le bilan moral.
  // Pas de notification — l'effet est volontairement discret : le joueur
  // s'en aperçoit en regardant son onglet ou en partageant un screenshot.
  useEffect(() => {
    const regime = moralRegime(worldState, pledges);
    if (document.title !== regime) {
      document.title = regime;
    }
  }, [worldState, pledges]);

  if (needsArchetype === true) {
    return (
      <ArchetypeSelector
        onSelect={(choice) => {
          persistArchetypeChoice(choice.id);
          resetInvestitureSeen();
          setNeedsArchetype(false);
        }}
      />
    );
  }

  if (status === "error") {
    return (
      <div className="boot-screen boot-error">
        <div>
          <div className="boot-title">Échec du chargement du moteur</div>
          <div className="boot-sub">{error}</div>
          <button
            type="button"
            className="boot-retry"
            onClick={() => window.location.reload()}
          >
            Recharger la page →
          </button>
        </div>
      </div>
    );
  }

  if (status !== "ready" || !worldState) {
    return (
      <div className="boot-screen">
        <div>
          <div className="boot-title">Moi Président</div>
          <div className="boot-sub">Chargement du moteur WebAssembly…</div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-shell">
      {showInvestiture && (
        <InvestitureOverlay onComplete={() => setShowInvestiture(false)} />
      )}
      <Cockpit
        state={worldState}
        headlines={headlines}
        lastRevelations={lastRevelations}
      />
    </div>
  );
}
